import { config } from "../../shared/config/environment";
import { fetchWithRefresh, type RefreshTokenFn } from "../../shared/api/fetchWithRefresh";
import type { Activity, Decision } from "../types";

// The background-HITL endpoints live on the Python FastAPI service
// (apps/backend/ai/API/routes/v1/agent_runtime.py), so this module routes
// through `aiApiBaseUrl`, mirroring src/chat/services/api.ts.

/**
 * Headers for backend API calls: JSON content type (when posting) and the
 * MSAL bearer token when present. Mirrors `buildChatHeaders` in
 * src/chat/services/api.ts.
 */
function buildHeaders(apiToken: string | null, contentType?: string): Record<string, string> {
  const headers: Record<string, string> = {};
  if (contentType) {
    headers["Content-Type"] = contentType;
  }
  if (apiToken) {
    headers["Authorization"] = `Bearer ${apiToken}`;
  }
  return headers;
}

/** Fetch the pending HITL decisions the signed-in user is allowed to act on. */
export async function listActivities(
  apiToken: string | null,
  refresh?: RefreshTokenFn,
): Promise<Activity[]> {
  const response = await fetchWithRefresh(
    `${config.aiApiBaseUrl}/api/v1/agent-runtime/activities?status=awaiting_input`,
    {
      headers: buildHeaders(apiToken),
      // The Office WebView can replay a stale GET; `no-store` keeps polling
      // honest so a just-resolved approval doesn't reappear from cache. We use
      // the fetch `cache` option (not a `Cache-Control` request header) on
      // purpose — a custom header would add `cache-control` to the CORS
      // preflight, which the APIM gateway's allow-list rejects.
      cache: "no-store",
    },
    refresh,
  );
  if (!response.ok) {
    throw new Error(`Failed to load approvals: ${response.status}`);
  }
  return response.json() as Promise<Activity[]>;
}

/**
 * Submit a human decision and enqueue the run's resume.
 *
 * The backend feeds `decision` verbatim to the parked `interrupt()`. The HITL
 * tool treats `ok: false` as a *frontend error* (it returns an error to the
 * agent), so a real human decision — approve OR reject — is always `ok: true`
 * with the chosen option in `data.decision`; the skill branches on that value.
 */
export async function resumeActivity(
  apiToken: string | null,
  activity: Pick<Activity, "run_id" | "interrupt_id">,
  decision: Decision,
  refresh?: RefreshTokenFn,
): Promise<void> {
  const data: Record<string, unknown> = { decision: decision.decision };
  if (decision.feedback) {
    data.feedback = decision.feedback;
  }
  if (decision.inputs && Object.keys(decision.inputs).length > 0) {
    data.inputs = decision.inputs;
  }
  const response = await fetchWithRefresh(
    `${config.aiApiBaseUrl}/api/v1/agent-runtime/resume`,
    {
      method: "POST",
      headers: buildHeaders(apiToken, "application/json"),
      body: JSON.stringify({
        run_id: activity.run_id,
        interrupt_id: activity.interrupt_id,
        decision: { ok: true, data },
      }),
    },
    refresh,
  );
  if (!response.ok) {
    throw new Error(`Failed to submit decision: ${response.status}`);
  }
}
